import { SerializationHelper } from '../utils/SerializationHelper'
import { AbstractModel } from './AbstractModel'
import { Customer } from './Customer'
import { CustomerNote } from './CustomerNote'
import { CustomerRallyRoundCrew } from './CustomerRallyRoundCrew'
import { CustomerRallyRoundPermission } from './CustomerRallyRoundPermission'
import { Rally } from './Rally'
import { RallyRound } from './RallyRound'
import { Team } from './Team'
import { Http } from '@/lib/utils/Http'

export class User extends AbstractModel {
    id: number|null = null
    firstname: string|null = null
    lastname: string|null = null
    customerGroupId: number|null = null
    customernr: number|null = null
    email: string|null = null
    street: string|null = null
    streetnumber: string|null = null
    zip: string|null = null
    city: string|null = null
    telephone: string|null = null
    annotiation: string|null = null
    country: string|null = null
    countryId: number|null = null
    teams: Team[] = []
    rounds: RallyRound[] = []
    rallyRoundPermissions: CustomerRallyRoundPermission[] = []
    crew: CustomerRallyRoundCrew[] = []
    notes: CustomerNote[] = []
    birthday: string|null = null;
    status: number|null = null;
    sex: number|null = null;
    customerReferrer: string|null = '';
    picture: string|null = null;
    socialSecurity: string|null = null;
    operatingArea: string|null = null;
    iban: string|null = null;
    tShirtSize: string|null = null;
    liabilitiesSigned = '-'

    // in crewlist
    lastTShirtSize: string|null = null

    public static readonly customerGroups = [
      { id: 1, name: 'Admin' },
      { id: 2, name: 'Mitarbeiter' },
      { id: 3, name: 'Team' },
      { id: 4, name: 'Zuschauer' }
    ]

    public static readonly customerReferrerTypes = [
      'Facebook',
      'Instagram',
      'Youtube',
      'TikTok',
      'Google',
      'Freunde/Bekannte',
      'War schon, mal dabei',
      'Presse/Zeitungen',
      'Fernsehen',
      'Hab ein Auto/Moped mit Aufkleber gesehen',
      'Essen Motorshow',
      'OLDTEMA Erfurt',
      'IMOT München',
      'andere'
    ]

    get isAdmin (): boolean {
      return this.customerGroupId === 1
    }

    get hasRallyRoundPermissions () {
      return this.rallyRoundPermissions.length > 0
    }

    get hasValidAddress () {
      return this.firstname &&
        this.lastname &&
        this.street &&
        this.zip &&
        this.city &&
        (this.country || this.countryId)
    }

    public getCustomerGroupName () {
      switch (this.customerGroupId) {
        case 1:
          return 'Admin'
        case 2:
          return 'Mitarbeiter'
        case 3:
          return 'Team'
        default:
          return 'Zuschauer'
      }
    }

    public async isInTeam (teamId: number): Promise<boolean> {
      const teamData = await (await Http.request(`/api/team/${teamId}/customer`)).json()
      if (!teamData) {
        return false
      }

      let isInTeam = false
      teamData.forEach((customer: Customer) => {
        if (customer && customer.customer_id === this.id) {
          isInTeam = true
        }
      })

      return isInTeam
    }

    public static async getAllForTeam (teamId: number): Promise<User[]> {
      let customersJson = await (
        await Http.request(`/api/team/${teamId}/customer/`)
      ).json()

      // some teams have not existing customers for some reason, filter them out
      customersJson = customersJson.filter((customerJson: any) => {
        if (customerJson) { return true }
      })

      return SerializationHelper.toInstanceArray(User, customersJson)
    }

    public static async search (search: string) {
      const res = await Http.request(`/api/customer/search/${search}`)
      const users = SerializationHelper.toInstanceArray(User, await res.json())

      users.map(user => {
        user.crew = SerializationHelper.toInstanceArray(CustomerRallyRoundCrew, user.crew)
      })

      return users
    }

    public static async getAllCrew (search = '', filterRally = -1, filterRallyRound = -1) {
      const res = await Http.request(`/api/customer/crew/${filterRally}/${filterRallyRound}/${search}`)
      const users = SerializationHelper.toInstanceArray(User, await res.json())

      users.map(user => {
        user.notes = SerializationHelper.toInstanceArray(CustomerNote, user.notes)
        user.crew = SerializationHelper.toInstanceArray(CustomerRallyRoundCrew, user.crew)
      })

      return users
    }

    public static async searchForRallyRound (search: string, rallyRoundId: number) {
      const res = await Http.request(`/api/rally-round/${rallyRoundId}/customer/${search}`)

      const users = SerializationHelper.toInstanceArray(User, await res.json())

      users.map(user => {
        user.crew = SerializationHelper.toInstanceArray(CustomerRallyRoundCrew, user.crew)
      })

      return users
    }

    public async changePassword (newPassword: string) {
      const res = await Http.request(`/api/customer/${this.id}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: newPassword
        })
      })
    }

    public async updatePermissions (customerGroupId: number|null, rallyRoundAdminIds: number[]|null, crew: CustomerRallyRoundCrew[]) {
      const res = await Http.request(`/api/customer/${this.id}/change-permission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerGroupId,
          rallyRoundAdminIds,
          crew
        })
      })
    }

    public async updateNotes () {
      return await Http.request(`/api/customer/${this.id}/notes`, {
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'PUT',
        body: JSON.stringify(this.notes)
      })
    }

    public async isTeamlead (teamId: number): Promise<boolean> {
      const res = await Http.request(`/api/team/is-teamlead/${teamId}`)
      return (await res.json()).teamlead
    }

    roundsAsString (delimeter = ', '): string {
      if (!this.rounds || !this.rounds.length) {
        return ''
      }
      return this.rounds.map(round => round.name).join(delimeter)
    }

    teamAsString (delimeter = ', '): string {
      if (!this.teams || !this.teams.length) {
        return '-'
      }
      // map team to team name
      const teams = this.teams
        .map((team) => team.name ?? '-')
      return teams.filter((name, i) => teams.indexOf(name) === i)
        .join(delimeter)
    }

    startnumberAsString (delimeter = ', '): string {
      if (!this.teams || !this.teams.length) {
        return '-'
      }

      // add # before number and only map from team to only starnumbers
      let startnumbers = this.teams.map((team) => `#${team.number}`)

      // remove duplicates in cool ES6 way https://www.samanthaming.com/tidbits/43-3-ways-to-remove-array-duplicates/
      startnumbers = startnumbers.filter(
        (number, i) => startnumbers.indexOf(number) === i
      )

      return startnumbers.join(delimeter)
    }

    crewAtAsString (rallies: Rally[], rallyRounds: RallyRound[]): string {
      let crewAt = ''
      this.crew.forEach(crew => { if (!crew.rallyRoundId) { return } crewAt += `${RallyRound.getRallyRoundNameById(crew.rallyRoundId, rallies, rallyRounds)} als ${crew.task}, ` })
      return crewAt.slice(0, -2)
    }

    rallyRoundCrewTask (rallyRoundId: number) {
      const crew = this.crew.find(crew => crew.rallyRoundId === rallyRoundId)
      if (!crew) {
        return '-'
      }
      return crew.task
    }
}
